<template>
  <div class="dashboard">
    <!-- 헤더 -->
    <div class="header">
      <div class="header-left">
        <h2>{{ selectedWard ? selectedWard + '병동' : (tenant.hospitalName || '전체') }}</h2>
        <h1>병동과 장비 선택</h1>
      </div>
      <div class="header-right">
    
      </div>
    </div>

    <!-- 병동 필터 버튼 -->
    <div class="ward-buttons">
      <button
        v-for="ward in wards"
        :key="ward"
        @click="filterWard(ward)"
        :class="['ward-button', { active: selectedWard === ward }]"
      >
        {{ ward }} 병동
      </button>
    </div>

    <!-- 로딩 상태 -->
    <div v-if="loading" class="loading">데이터 불러오는 중...</div>

    <!-- 장비 카드 그리드 -->
    <div v-else class="card-grid">
      <div 
        v-for="device in deviceTypes" 
        :key="device.type" 
        class="card"
        @click="goToTagList(device.type)"
      >
        <!-- 이미지 배경과 오버레이 텍스트 -->
        <div class="card-image-container">
          <img
            :src="device.image || defaultImage"
            alt="장비 이미지"
            class="device-image"
            @error="onImageError"
          />
          <!-- 오버레이 그라데이션 -->
          <div class="image-overlay"></div>
          
          <!-- 장비 이름 (이미지 위에 표시) -->
          <h3 class="device-name-overlay">{{ device.name }}</h3>
          
          <!-- 사용가능 개수 (이미지 위에 표시) -->
          <p class="device-availability-overlay">
            사용가능 기구 {{ getAvailableCount(device.type) }}개
            <span class="status-dot" :style="{ backgroundColor: device.color }"></span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import tenantState, { wardByCode } from "@/tenant";

const router = useRouter();
const tenant = tenantState.tenant;               // 병동 목록은 테넌트 프리셋에서
const wards = tenant.wards.map((w) => w.code);
const selectedWard = ref(wards[0] || "");
const loading = ref(true);
const tags = ref([]);
const defaultImage = "/images/default-device.png";

// 9개 장비 타입 정의 - require() 사용
const deviceTypes = [
  { type: "DVT", name: "DVT", image: require("@/assets/dvt.png"), color: "#16a34a" },
  { type: "PulseOxi", name: "PulseOxi", image: require("@/assets/pulse-oxi.png"), color: "#ec4899" },
  { type: "SCD_P", name: "SCD_P", image: require("@/assets/scd.png"), color: "#16a34a" },
  { type: "Nebulizer", name: "Nebulizer", image: require("@/assets/nebulizer.png"), color: "#16a34a" },
  { type: "PatientM", name: "PatientM", image: require("@/assets/patient-monitor.png"), color: "#9333ea" },
  { type: "InfusionP", name: "InfusionP", image: require("@/assets/infusion-pump.png"), color: "#ec4899" },
  { type: "VarioS", name: "VarioS", image: require("@/assets/varios.png"), color: "#ea580c" },
  { type: "FeedingP", name: "FeedingP", image: require("@/assets/feeding-pump.png"), color: "#ea580c" },
  { type: "HPO", name: "HPO", image: require("@/assets/hpo.png"), color: "#dc2626" }
];

const fetchData = async () => {
  try {
    loading.value = true;
    const company = localStorage.getItem("company") || "sdaa";
    const res = await axios.get("/api/dashboardSDAA", {
      params: { company },
    });
    tags.value = Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    console.error("데이터 불러오기 실패:", err);
    tags.value = [];
  } finally {
    loading.value = false;
  }
};

// 선택된 병동의 장비별 사용가능 개수 계산 (병동별 위치+프리픽스 규칙 적용)
const getAvailableCount = (deviceType) => {
  if (!Array.isArray(tags.value)) return 0;

  // 병동별 보관 위치·접두사는 테넌트 프리셋 (병동이 없으면 전체 집계)
  const ward = wardByCode(tenant, selectedWard.value);
  const allowedLocations = ward ? ward.storage : null;
  const wardPrefix = ward ? ward.prefix : '';

  return tags.value.filter(tag => {
    if (!tag || !tag.devicename || !tag.devicetype) return false;

    // 병동 프리픽스 체크
    const wardPrefixMatch = !wardPrefix || String(tag.devicename).startsWith(wardPrefix);

    // 병동별 허용 위치 체크
    const tagLoc = String(tag.taglocation || '');
    const locationMatch = !allowedLocations || allowedLocations.includes(tagLoc);

    // 장비 타입 필터 (포함 여부)
    const deviceMatch = String(tag.devicetype || '').includes(deviceType);

    // 활성 상태 확인
    const isActive = tag.active === 0;

    return wardPrefixMatch && locationMatch && deviceMatch && isActive;
  }).length;
};

const filterWard = (ward) => {
  selectedWard.value = ward;
};

const goToTagList = (deviceType) => {
  // TagList 페이지로 이동하면서 검색 파라미터 전달
  let searchDeviceType = deviceType;
  
  // 특별한 경우 처리
  if (deviceType === 'InfusionP') {
    searchDeviceType = 'Infusion_P';
  } else if (deviceType === 'PatientM') {
    searchDeviceType = 'Patient_M';
  } else if (deviceType === 'FeedingP') {
    searchDeviceType = 'Feeding_P';
  } else if (deviceType === 'VarioS') {
    searchDeviceType = 'Vario_Suction';
  } else if (deviceType === 'PulseOxi') {
    searchDeviceType = 'Pulse_Oxi';
  } else if (deviceType === 'HPO') {
    searchDeviceType = 'H_Pulse_Oxi';
  }
  
  const searchQuery = selectedWard.value ? `${selectedWard.value}_${searchDeviceType}` : searchDeviceType;
  console.log('검색 쿼리:', searchQuery); // 디버깅용
  
  router.push({
    name: 'TagList', // TagList 라우트 이름
    query: { 
      searchText: searchQuery,
      company: localStorage.getItem("company") || "sdaa"
    }
  });
};

const onImageError = (event) => {
  event.target.src = defaultImage;
};

onMounted(fetchData);
</script>

<style scoped>
.dashboard {
  padding: 24px;
  background-color: #f9fafb;
  min-height: 100vh;
  box-sizing: border-box;
  position: relative;
  overflow-x: hidden;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.header-left h2 {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.header-left h1 {
  font-size: 28px;
  font-weight: bold;
  color: #111827;
  margin: 0;
}

.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.hospital-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
}

.hospital-icon {
  font-size: 18px;
}

.hospital-name {
  font-weight: 500;
}

.header-icons {
  display: flex;
  gap: 16px;
}

.icon {
  font-size: 18px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.icon:hover {
  opacity: 0.7;
}

.ward-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
  flex-wrap: wrap;
  justify-content: center;
}

.ward-button {
  padding: 12px 24px;
  border: 1px solid #d1d5db;
  background-color: #f9fafb;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  color: #6b7280;
}

.ward-button.active {
  background-color: #3b82f6;
  color: white;
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.ward-button:hover:not(.active) {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.loading {
  text-align: center;
  color: #6b7280;
  font-size: 16px;
  margin-top: 40px;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 400px;
}

.card {
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden; /* 이미지가 카드 경계를 넘지 않도록 */
  height: 280px;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: #3b82f6;
}

.card-image-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.device-image {
  width: 100%;
  height: 100%;
  object-fit: cover; /* 이미지를 카드에 꽉 채움 */
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0.3) 50%,
    rgba(0, 0, 0, 0.7) 100%
  );
  pointer-events: none;
}

.device-name-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
  z-index: 2;
}

.device-availability-overlay {
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  margin: 0;
  font-size: 16px;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .card-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .header {
    flex-direction: column;
    gap: 16px;
  }
  
  .header-right {
    align-items: flex-start;
  }
  
  .card-grid {
    grid-template-columns: 1fr;
  }
  
  .ward-buttons {
    justify-content: center;
  }
  
  .device-name-overlay {
    font-size: 20px;
    top: 15px;
    left: 15px;
    right: 15px;
  }
  
  .device-availability-overlay {
    bottom: 15px;
    left: 15px;
    right: 15px;
    font-size: 14px;
  }
}

@media (max-width: 640px) {
  .dashboard {
    padding: 16px;
  }
  
  .header-left h1 {
    font-size: 24px;
  }
  
  .card {
    height: 240px;
  }
  
  .device-name-overlay {
    font-size: 18px;
    top: 12px;
    left: 12px;
    right: 12px;
  }
  
  .device-availability-overlay {
    bottom: 12px;
    left: 12px;
    right: 12px;
    font-size: 13px;
  }
}
</style>